import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BottomNav, Screen, type NavItem } from '@promtver/ui-kit';

import { useAuth } from '../auth/AuthProvider';
import { ru } from '../i18n/ru';

export interface AppShellProps {
  title: string;
  subtitle?: string;
  /** Ключ активного раздела нижней навигации. */
  active: string;
  headerRight?: ReactNode;
  children: ReactNode;
}

interface Section {
  key: string;
  to: string;
  label: string;
}

/**
 * Каркас панели предприятия: мобильный-first (ТЗ FR-MGR-06).
 * Нижняя навигация — 5 пунктов, зона нажатия не менее 44 px (ТЗ §8.1).
 * Дополнительно — выдвижное меню со всеми разделами (6 пунктов).
 */
export function AppShell({ title, subtitle, active, headerRight, children }: AppShellProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  const sections: Section[] = [
    { key: 'scanner', to: '/scanner', label: ru.nav.scanner },
    { key: 'schedule', to: '/schedule', label: ru.nav.schedule },
    { key: 'group', to: '/group', label: ru.nav.group },
    { key: 'reports', to: '/reports', label: ru.nav.reports },
    { key: 'programs', to: '/programs', label: ru.nav.programs },
    { key: 'cabinet', to: '/cabinet', label: ru.nav.cabinet },
  ];

  useEffect(() => {
    if (!menuOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [menuOpen]);

  const go = (to: string) => {
    setMenuOpen(false);
    navigate(to);
  };

  const bottomItems: NavItem[] = [
    { key: 'scanner', label: ru.nav.scanner, active: active === 'scanner' },
    { key: 'schedule', label: ru.nav.schedule, active: active === 'schedule' },
    { key: 'group', label: ru.nav.group, active: active === 'group' },
    { key: 'reports', label: ru.nav.reports, active: active === 'reports' },
    { key: 'cabinet', label: ru.nav.cabinet, active: active === 'cabinet' },
  ];

  return (
    <>
      <Screen
        title={title}
        subtitle={subtitle}
        headerRight={
          <div className="flex items-center gap-2">
            {headerRight}
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              aria-haspopup="dialog"
              aria-expanded={menuOpen}
              className="min-h-touch rounded-button border border-line px-3 text-sm font-semibold text-content"
            >
              {ru.nav.menu}
            </button>
          </div>
        }
        bottomNav={<BottomNav items={bottomItems} onSelect={(key) => go(`/${key}`)} />}
      >
        {children}
      </Screen>

      {menuOpen ? (
        <div
          className="fixed inset-0 z-50 flex flex-col bg-ink-900/95 p-4"
          role="dialog"
          aria-modal="true"
          aria-label={ru.nav.sections}
        >
          <div className="mb-4 flex items-center justify-between gap-3">
            <strong className="text-base">{ru.nav.sections}</strong>
            <button
              type="button"
              onClick={() => setMenuOpen(false)}
              className="min-h-touch rounded-button border border-line px-4 text-sm font-semibold"
            >
              {ru.nav.close}
            </button>
          </div>

          <nav className="flex flex-col gap-2">
            {sections.map((section) => (
              <button
                key={section.key}
                type="button"
                onClick={() => go(section.to)}
                aria-current={location.pathname === section.to ? 'page' : undefined}
                className={`min-h-touch rounded-button border px-4 text-left text-sm font-semibold ${
                  location.pathname === section.to
                    ? 'border-accent bg-accent/10 text-accent'
                    : 'border-line text-content'
                }`}
              >
                {section.label}
              </button>
            ))}
          </nav>

          <div className="mt-auto flex flex-col gap-3 pt-6">
            <span className="text-xs text-muted">
              {user?.displayName ?? user?.email ?? ru.app.panel}
            </span>
            <button
              type="button"
              onClick={() => {
                setMenuOpen(false);
                logout();
                navigate('/login');
              }}
              className="min-h-touch rounded-button border border-danger px-4 text-sm font-semibold text-danger"
            >
              {ru.nav.logout}
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}
